import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccountOpportunities } from "@/hooks/api/use-account-opportunities";
import {
  useCreateMilestoneAnalysisTask,
  useMilestoneAnalyses,
} from "@/hooks/api/use-milestone-analyses";
// Removed unused import
import { useBuyingGroupSelection } from "@/hooks/use-buying-group-selection";
import { OpportunityCard } from "./opportunity-card";

interface MilestoneAnalysesViewProps {
  accountId?: string;
  selectedOpportunityIds: string[];
  onFilterCitedTouchpoints?: (taskId: string, touchpointIds: string[]) => void;
}

export default function MilestoneAnalysesView({
  accountId,
  selectedOpportunityIds,
  onFilterCitedTouchpoints,
}: MilestoneAnalysesViewProps) {
  // Query client is now used only in the hooks
  const posthog = usePostHog();

  // Import the buying group selection hook with dual-filtering approach
  // In custom mode, we use explicit opportunity IDs from activeCustomGroupOpportunityIds
  // This ensures consistency with the touchpoint table and minimap views
  const {
    buyingGroupMode,
    selectedPersonIds,
    activeCustomGroupOpportunityIds,
  } = useBuyingGroupSelection();

  // Fetch milestone analysis data
  const { data: taskGroups, isLoading: isLoadingAnalyses } =
    useMilestoneAnalyses(
      accountId,
      buyingGroupMode === "custom"
        ? activeCustomGroupOpportunityIds
        : selectedOpportunityIds.length > 0
          ? selectedOpportunityIds
          : undefined,
      buyingGroupMode, // Pass buying group mode parameter
      true, // enabled
    );

  // Fetch all opportunities for this account
  const { data: opportunities, isLoading: isLoadingOpps } =
    useAccountOpportunities(
      accountId,
      selectedOpportunityIds.length > 0 ? selectedOpportunityIds : undefined,
      selectedPersonIds.length > 0 ? selectedPersonIds : undefined, // Pass person IDs for custom buying groups
      buyingGroupMode, // Pass buying group mode
      // Pass custom group opportunity IDs when in custom mode
      buyingGroupMode === "custom"
        ? activeCustomGroupOpportunityIds
        : undefined,
    );

  // Create milestone analysis mutation
  const createAnalysisTask = useCreateMilestoneAnalysisTask();

  // Track recently requested tasks
  const [justRequestedTaskIds, setJustRequestedTaskIds] = useState<Set<string>>(
    new Set(),
  );

  // Clear the requested task IDs after 2 minutes (cleanup)
  // This prevents stale UI state if the user stays on the page for a long time
  useEffect(() => {
    const clearRequestedTasksTimeout = setTimeout(() => {
      if (justRequestedTaskIds.size > 0) {
        setJustRequestedTaskIds(new Set());
      }
    }, 120000); // 2 minutes

    return () => {
      clearTimeout(clearRequestedTasksTimeout);
    };
  }, [justRequestedTaskIds]);

  // Get task groups for a specific opportunity
  const getTaskGroupsForOpportunity = (opportunityId: string) => {
    if (!taskGroups) return [];
    return taskGroups.filter((group) => group.opportunity_id === opportunityId);
  };

  // Handle requesting a new analysis
  const handleRequestAnalysis = (opportunityId: string) => {
    // Find the opportunity to get additional context for analytics
    const opportunity = opportunities?.find((opp) => opp.id === opportunityId);

    createAnalysisTask.mutate(
      {
        opportunity_id: opportunityId,
        analysis_type: "opportunity_activation",
      },
      {
        onSuccess: (data) => {
          // Add task to set of just-requested tasks to update UI immediately
          setJustRequestedTaskIds((prev) => {
            const newSet = new Set(prev);
            newSet.add(data.task_id);
            return newSet;
          });
          posthog?.capture("analysis_task_created", {
            analysis_type: "opportunity_activation",
            opportunity_id: opportunityId,
            opportunity_name: opportunity?.name,
            account_id: accountId,
            buying_group_mode: buyingGroupMode,
          });
        },
      },
    );
  };

  const isLoading = isLoadingAnalyses || isLoadingOpps;

  // Sort opportunities by creation date (oldest first)
  const sortedOpportunities = opportunities
    ? [...opportunities].sort(
        (a, b) =>
          new Date(a.created_date).getTime() -
          new Date(b.created_date).getTime(),
      )
    : [];

  // Get admin flag for use in OpportunityCard component

  return (
    <div className="flex flex-col h-full">
      {/* Milestone Analysis Cards */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="grid gap-3 grid-cols-1 pb-4 pt-4">
          {isLoading ? (
            // Loading skeleton
            <Card className="p-6">
              <Skeleton className="h-7 w-1/2 mb-2" />
              <Skeleton className="h-4 w-1/4 mb-6" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </Card>
          ) : sortedOpportunities.length > 0 ? (
            // Render all opportunities with a consistent container
            sortedOpportunities.map((opportunity) => (
              <OpportunityCard
                key={opportunity.id}
                opportunity={{
                  id: opportunity.id,
                  name: opportunity.name,
                  type: opportunity.type,
                  stage_name: opportunity.stage_name,
                  is_closed: opportunity.is_closed,
                  is_won: opportunity.is_won,
                  created_date: opportunity.created_date,
                  close_date: opportunity.close_date || undefined,
                }}
                taskGroups={getTaskGroupsForOpportunity(opportunity.id)}
                accountId={accountId}
                onFilterCitedTouchpoints={onFilterCitedTouchpoints}
                onRequestAnalysis={handleRequestAnalysis}
                requestMutation={createAnalysisTask}
                requestedTaskIds={justRequestedTaskIds}
              />
            ))
          ) : (
            // Empty state when no opportunities
            <div className="col-span-2">
              <EmptyState
                title="No opportunities found"
                description="There are no opportunities for this account or the selected filter."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
